import { Navigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import { useAuth } from '@/hooks/useAuth';
import { PasswordChangeModal } from './PasswordChangeModal';
import truckAnimation from '@/animations/truck-loading.json';

// Logo path (from public folder)
const GPC_LOGO = '/2020.05.20-GPC_logo-transbg-3508px.png';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function LoadingScreen() {
  return (
    <div style={{
      background: 'linear-gradient(180deg, var(--color-bg-primary), var(--color-bg-secondary))',
      minHeight: '100vh',
      padding: '16px',
      width: '100%',
      boxSizing: 'border-box',
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px',
        padding: '60px 20px',
        minHeight: '60vh',
      }}>
        {/* Company Logo with glow effect */}
        <div style={{ animation: 'logoGlow 2s ease-in-out infinite' }}>
          <img
            src={GPC_LOGO}
            alt="GPC - Gardermoen Perishable Center"
            style={{
              maxWidth: '380px',
              width: '100%',
              height: 'auto',
              filter: 'drop-shadow(0 4px 12px rgba(0, 51, 204, 0.3))',
            }}
          />
        </div>

        {/* Truck Animation */}
        <div style={{ width: '200px', height: '100px' }}>
          <Lottie
            animationData={truckAnimation}
            loop={true}
            autoplay={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        {/* Loading Text */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '18px',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            marginBottom: '8px',
          }}>
            Loading shifts...
          </div>
          <div style={{
            color: 'var(--color-text-tertiary)',
            fontSize: '13px',
            animation: 'pulse 2s ease-in-out infinite',
          }}>
            Preparing your schedule
          </div>
        </div>

        {/* Loading bar */}
        <div style={{
          width: '240px',
          height: '4px',
          background: 'var(--color-bg-tertiary)',
          borderRadius: '2px',
          overflow: 'hidden',
        }}>
          <div style={{
            width: '40%',
            height: '100%',
            background: 'linear-gradient(90deg, var(--color-info), var(--color-info))',
            borderRadius: '2px',
            animation: 'loadingBar 1.5s ease-in-out infinite',
          }} />
        </div>
      </div>
      <style>{`
        @keyframes logoGlow {
          0%, 100% { filter: drop-shadow(0 4px 12px rgba(0, 51, 204, 0.3)); }
          50% { filter: drop-shadow(0 4px 20px rgba(0, 102, 204, 0.4)); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes loadingBar {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(150%); }
          100% { transform: translateX(150%); }
        }
      `}</style>
    </div>
  );
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (profile?.password_change_required) {
    return <PasswordChangeModal />;
  }

  return <>{children}</>;
}
